// The Threads archive — a searchable list of all customer-anchored threads,
// open (on the rail) and completed. Tapping a thread re-opens it on the Home
// workspace via a ?thread=ID deep-link (HomeV2 reads the param).
//
// v1 covers the archive + search + open/completed grouping with revenue vs
// "record only". (Folding the SMS Messaging tab in is a separate follow-up.)

import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'

import { formatMoney } from '@/lib/format'
import { formatRelative } from '@/lib/dates'
import { fetchRail, threadKeys, type ThreadRailItem } from '@/lib/threads'

export function ThreadsArchive() {
  const { getToken, isLoaded } = useAuth()
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const [q, setQ] = useState('')

  const query = useQuery({
    queryKey: threadKeys.archive(q),
    enabled: isLoaded,
    queryFn: () => fetchRail(getToken, 'all', q),
    staleTime: 10_000,
  })
  const all = query.data?.threads ?? []
  const open = all.filter((x) => x.status === 'open')
  const completed = all.filter((x) => x.status === 'closed')

  const openThread = (id: string) =>
    navigate(`/?thread=${encodeURIComponent(id)}`)

  const renderGroup = (label: string, rows: ThreadRailItem[]) => (
    <section>
      <h2 className="mb-2 text-[13px] font-semibold text-muted-foreground">
        {label}
      </h2>
      {/* §4.8 list rows in a 14px-radius bordered container */}
      <ul className="divide-y divide-border overflow-hidden rounded-[14px] border border-border bg-card">
        {rows.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => openThread(r.id)}
              className="flex min-h-[60px] w-full items-center justify-between gap-3 px-3.5 py-3 text-left transition-[background-color,transform] active:scale-[0.99] active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
            >
              <span className="min-w-0">
                <span className="block truncate text-[15px] font-[620] text-foreground">
                  {r.customer_name ?? r.title ?? t('home_v2.new_chat')}
                </span>
                {r.vehicle_label ? (
                  <span className="block truncate text-[13px] text-muted-foreground">
                    {r.vehicle_label}
                  </span>
                ) : null}
              </span>
              <span className="shrink-0 text-right font-mono text-[12px] text-ink-3">
                {r.status === 'closed'
                  ? r.revenue_cad != null
                    ? formatMoney(r.revenue_cad)
                    : t('threads.record_only')
                  : r.last_message_at
                    ? formatRelative(r.last_message_at)
                    : t('threads.open_now')}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  )

  return (
    <div data-surface="home-v2" className="mx-auto max-w-2xl space-y-6">
      {/* §4.5 page header */}
      <header className="space-y-0.5">
        <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
          {t('threads.title')}
        </h1>
        <p className="text-[13px] text-muted-foreground">{t('threads.subtitle')}</p>
      </header>

      {/* §4.10 search input: 48px, 14px radius, leading ink-3 icon */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('threads.search_placeholder')}
          className="h-12 w-full rounded-[14px] border border-border bg-card pl-9 pr-3 text-base text-foreground outline-none focus:border-muted-foreground/40"
        />
      </div>

      {query.isLoading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {t('threads.loading')}
        </p>
      ) : all.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {t('threads.empty')}
        </p>
      ) : (
        <div className="space-y-6">
          {open.length > 0 ? renderGroup(t('threads.group_open'), open) : null}
          {completed.length > 0
            ? renderGroup(t('threads.group_completed'), completed)
            : null}
        </div>
      )}
    </div>
  )
}
